/* eslint-disable react-hooks/exhaustive-deps */
import { useFormik } from 'formik';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import Select from 'react-select';
import { Card, CardFooter } from '@components/card/card';
import { CardBody, Form, InputGroup, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useLookupCurrency from '@hooks/Lookups/use-Lookup-Currency';
import Breadcrumb from '@components/Shared/Breadcrumb';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import SwalAlert from '@components/Shared/Alert/SwalAlert';
import { useNavigate } from 'react-router-dom';
import Loading from '@components/Shared/Loader/Loading';
import { TreeSelect } from 'antd';
import useGetPaymentTermsList from '@hooks/Sales/useGetPaymentTermsList';
import useGetPaymentMethod from '@hooks/Payrolls/use-Get-PaymentMethod';
import ArReceiptSchedules from './ArReceiptSchedules';
import ArTransactionsLine from './ArTransactionsLine';
import { AddArTransactionSchema } from '../../../ValidationForm/validationSchema';
import { useEffect, useState } from 'react';
import ToastSwal from '@components/Shared/Alert/ToastSwal';
import useGetUnApplyPrepaymentsAr from '@hooks/AR/ArReceipts/useGetUnApplyPrepayments';
import useGetCustomersList from '@hooks/NTX/Peoples/useGetCustomersList';
import { addArTransactions } from '@store/Finance/AR/ArTransactions/ArTransactionsSlice';
import useGetPeopleLocationsList from '@hooks/NTX/Peoples/useGetPeopleLocationsList';
import useGetPersons from '@hooks/HR/use-get-persons';
import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';
import { activeFinanceSelector } from '@store/Login/authSelectors';
import { useDailyRate } from '@hooks/Finance/DailyRates/useDailyRate';

const AddArTransactions = () => {
  const [showModal, setShowModal] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedOperatingUnit, setSelectedOperatingUnit] = useState(null);
  const [activeTab, setActiveTab] = useState('arTransactionLines');

  //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // البيانات المحملة من الـ hooks
  const { Currency, loading: currencyLoading, error: currencyError } = useLookupCurrency();
  const { OperatingUnitOptions, loading: ouLoading, error: ouError } = useGetOperatingUnitWithPermission();
  const { treeCustomerData, loading: customersLoading, error: customersError, OptionCustomers } = useGetCustomersList();
  const { PaymentTermsList, loading: termsLoading, error: termsError } = useGetPaymentTermsList();
  const { PaymentMethodList, loading: methodLoading, error: methodError } = useGetPaymentMethod();
  const { dataEmployees, loading: employeesLoading, error: employeesError } = useGetPersons();
  const activeFinance = useAppSelector(activeFinanceSelector);

  //#region Formik
  const formik = useFormik({
    initialValues: {
      transactionNum: '',
      peopleId: '',
      transactionType: '',
      referenceType: '',
      referenceId: '',
      transactionDate: new Date().toISOString().split('T')[0],
      organizationId: '',
      paymentTermsId: '',
      paymentMethodId: '',
      currencyCode: 'SAR',
      currencyConversionRate: 1,
      includeVatFlag: true,
      salesrepId: '',
      billToCustomerId: '',
      shipToCustomerId: '',
      arTransactionLinesList: [],
      arReceiptSchedulesList: []
    },

    enableReinitialize: true,
    validationSchema: AddArTransactionSchema(t, activeFinance),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        // التحقق من وجود بنود الفاتورة
        if (!values.arTransactionLinesList?.length) {
          SwalAlert({ text: t('لا يمكن متابعة العملية بدون إضافة بنود للفاتورة، يرجى إضافة البنود.') });
          setSubmitting(false);
          return;
        }

        // التحقق من تطابق المبالغ
        const totalTransactionLines = values.arTransactionLinesList?.reduce(
          (sum: number, item: any) => sum + (item.total || 0),
          0
        );
        const totalReceiptSchedules = values.arReceiptSchedulesList?.reduce(
          (sum: number, payment: any) => sum + Number(payment.amount || 0),
          0
        );

        if (values.arReceiptSchedulesList?.length > 0 && totalTransactionLines !== totalReceiptSchedules) {
          SwalAlert({ text: t('لا يتطابق مجموع الفاتورة مع مجموع الجدولة. يرجى المراجعة.') });
          setSubmitting(false);
          return;
        }

        // إعداد البيانات للإرسال
        const submitData = {
          transactionNum: values.transactionNum,
          peopleId: values.peopleId,
          transactionType: values.transactionType,
          salesrepId: values.salesrepId,
          transactionDate: values.transactionDate,
          billToCustomerId: values.billToCustomerId,
          shipToCustomerId: values.shipToCustomerId,
          transactionAmount: totalTransactionLines,
          currencyCode: values.currencyCode,
          currencyConversionRate: values.currencyConversionRate || 1,
          paymentTermsId: values.paymentTermsId,
          paymentMethodId: values.paymentMethodId,
          dueDate: null,
          vatAmount: 0,
          paymentFlag: true,
          referenceId: values.referenceId || null,
          organizationId: values.organizationId,
          arTransactionLinesList: values.arTransactionLinesList?.map((line: any, idx) => ({
            lineNumber: idx + 1,
            lineType: line.lineType,
            invItemId: line.invItemId,
            uom: line.uom || '5cc5ff20-b711-4e46-a9ad-0f6de4b81b1a',
            description: line.description,
            price: line.price || 0,
            qty: line.qty || 0,
            discountRate: line.discountRate || 0,
            discountAmount: line.discountAmount || 0,
            vatRate: line.vatRate || 0,
            total: line.total || 0,
            vatAmount: line.vatAmount || 0,
            accountId: line.accountId,
            costCenterId: line.costCenterId,
            unitCost: 0,
            salHeaderId: null,
            salLineId: null
          })),
          arReceiptSchedulesList:
            values.arReceiptSchedulesList?.map((item: any) => ({
              amount: item.amount,
              dueDate: item.dueDate,
              receiptId: item.paymentId
            })) || null
        };

        // إرسال البيانات
        const result = await dispatch(addArTransactions(submitData)).unwrap();

        if (result?.succeeded) {
          CustomAlert({ action: 'Add' });
          resetForm();
          navigate('/Ar/ArTransactions');
        } else {
          CustomAlert({ action: 'Error', msg: result?.message });
        }
      } catch (error) {
        CustomAlert({ action: 'Error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const { hasPrepayments, totalPrepaymentAmount, UnApplyPrepaymentsList } = useGetUnApplyPrepaymentsAr({
    PeopleId: formik.values.peopleId
  });

  const { billToLocation, shipToLocation } = useGetPeopleLocationsList({
    peopleId: formik.values.peopleId
  });

  const toNum = (v: any) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  // معالجة إرسال النموذج في المودال
  const handleSubmitModal = () => {
    if (selectedCustomer && selectedOperatingUnit && formik.values.transactionType) {
      formik.setValues({
        ...formik.values,
        peopleId: selectedCustomer,
        organizationId: selectedOperatingUnit
      });
      setShowModal(false);
    } else {
      ToastSwal({ title: t('يرجى اختيار الوحدة التشغيلية والعميل ونوع الفاتورة'), timer: 1000 });
    }
  };

  const {
    rate,
    loading: loadingRate,
    error: errorRate,
    refetch
  } = useDailyRate({
    currencyCode: formik.values.currencyCode,
    date: formik.values.transactionDate
  });

  useEffect(() => {
    if (!formik.values.currencyCode) {
      if (toNum(formik.values.currencyConversionRate) !== 1) formik.setFieldValue('currencyConversionRate', 1, false);
      return;
    }

    if (!loadingRate && !errorRate) {
      const newRate = toNum(rate) || 1;
      if (toNum(formik.values.currencyConversionRate) !== newRate) {
        formik.setFieldValue('currencyConversionRate', newRate, false);
      }
    }
  }, [
    formik.values.currencyCode,
    formik.values.transactionDate,
    formik.values.currencyConversionRate,
    loadingRate,
    errorRate,
    rate
  ]);

  console.log(formik.values.transactionType);

  // حالات التحميل والأخطاء المجمعة
  const isLoading =
    currencyLoading || ouLoading || customersLoading || termsLoading || methodLoading || employeesLoading;
  const hasError = currencyError || ouError || customersError || termsError || methodError || employeesError;

  return (
    <>
      <Breadcrumb
        BreadcrumbList={[
          { name: 'Home', link: ':moduleHome' },
          { name: 'ReceivableInvoices', link: '/AR/ArTransactions' },
          { name: 'AddNewInvoice', link: null }
        ]}
        PageName={t('AddNewInvoice')}
        subtitle={OptionCustomers?.find(f => f.id === formik.values.peopleId)?.label}
      />

      {!showModal && (
        <>
          <Card>
            <Loading loading={isLoading} error={hasError}>
              <Form onSubmit={formik.handleSubmit}>
                <CardBody>
                  <div className='row'>
                    {/* تاريخ الفاتورة */}
                    <div className='col-lg-3 col-md-6 mb-3'>
                      <label className='form-label'>{t('Date')}</label>
                      <Form.Control
                        type='date'
                        name='transactionDate'
                        onChange={formik.handleChange}
                        value={formik.values.transactionDate}
                        isInvalid={formik.touched.transactionDate && !!formik.errors.transactionDate}
                      />
                      <Form.Control.Feedback type='invalid'>{formik.errors.transactionDate}</Form.Control.Feedback>
                    </div>

                    {/* نوع الفاتورة */}
                    <div className='col-lg-3 col-md-6 mb-3'>
                      <label className='form-label'>{t('InvoiceType')}</label>
                      <Form.Select
                        disabled={!showModal}
                        name='transactionType'
                        onChange={formik.handleChange}
                        value={formik.values.transactionType}
                        isInvalid={formik.touched.transactionType && !!formik.errors.transactionType}
                      >
                        <option value=''>{t('Choose')}</option>
                        <option value='Standard'>{t('Standard')}</option>
                        <option value='CreditMemo'>{t('CreditMemo')}</option>
                      </Form.Select>
                      <Form.Control.Feedback type='invalid'>{formik.errors.transactionType}</Form.Control.Feedback>
                    </div>

                    {/* الوحدة التشغيلية */}
                    <div className='col-lg-3 col-md-6 mb-3'>
                      <label className='form-label'>{t('OperatingUnit')}</label>
                      <Select
                        isDisabled
                        isLoading={ouLoading}
                        options={OperatingUnitOptions}
                        value={OperatingUnitOptions?.find((f: any) => f.value === formik.values.organizationId)}
                        placeholder={t('Choose')}
                        classNamePrefix='react-select'
                      />
                      <div className='text-danger small'>
                        {formik.touched.organizationId && formik.errors.organizationId}
                      </div>
                    </div>

                    {/* العميل */}
                    <div className='col-lg-3 col-md-6 mb-3'>
                      <label className='form-label'>{t('Customer')}</label>
                      <TreeSelect
                        disabled
                        loading={customersLoading}
                        className='w-100'
                        placeholder={t('Choose')}
                        value={formik.values.peopleId}
                        onChange={value => formik.setFieldValue('peopleId', value)}
                        treeData={treeCustomerData}
                        treeNodeFilterProp='title'
                        showSearch
                        allowClear
                      />
                      <div className='text-danger small'>{formik.touched.peopleId && formik.errors.peopleId}</div>
                    </div>

                    {/* موقع الشحن */}
                    <div className='col-lg-3 col-md-6 mb-3'>
                      <label className='form-label'>{t('ShippingLocation')}</label>
                      <Form.Select
                        name='shipToCustomerId'
                        onChange={formik.handleChange}
                        value={formik.values.shipToCustomerId}
                        isInvalid={formik.touched.shipToCustomerId && !!formik.errors.shipToCustomerId}
                      >
                        <option value=''>{t('Choose')}</option>
                        {shipToLocation?.map((item: any) => (
                          <option key={item.id} value={item.id}>
                            {i18n.language === 'en' ? item.name : item.name2}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type='invalid'>{formik.errors.shipToCustomerId}</Form.Control.Feedback>
                    </div>

                    {/* موقع الفوترة */}
                    <div className='col-lg-3 col-md-6 mb-3'>
                      <label className='form-label'>{t('BillingLocation')}</label>
                      <Form.Select
                        name='billToCustomerId'
                        onChange={formik.handleChange}
                        value={formik.values.billToCustomerId}
                        isInvalid={formik.touched.billToCustomerId && !!formik.errors.billToCustomerId}
                      >
                        <option value=''>{t('Choose')}</option>
                        {billToLocation?.map((item: any) => (
                          <option key={item.id} value={item.id}>
                            {i18n.language === 'en' ? item.name : item.name2}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type='invalid'>{formik.errors.billToCustomerId}</Form.Control.Feedback>
                    </div>

                    {/* رقم فاتورة العميل */}
                    <div className='col-lg-3 col-md-6 mb-3'>
                      <label className='form-label'>{t('CustomerInvoiceNumber')}</label>
                      <Form.Control
                        type='text'
                        name='transactionNum'
                        onChange={formik.handleChange}
                        value={formik.values.transactionNum}
                        isInvalid={formik.touched.transactionNum && !!formik.errors.transactionNum}
                        // placeholder={t('EnterInvoiceNumber')}
                      />
                      <Form.Control.Feedback type='invalid'>{formik.errors.transactionNum}</Form.Control.Feedback>
                    </div>

                    {/* مندوب المبيعات */}
                    <div className='col-lg-3 col-md-6 mb-3'>
                      <label className='form-label'>{t('SalesRepresentative')}</label>
                      <Select
                        classNamePrefix='react-select'
                        isLoading={employeesLoading}
                        isSearchable={true}
                        isClearable
                        name='salesrepId'
                        options={dataEmployees}
                        value={dataEmployees?.find((f: any) => f.value === formik.values.salesrepId)}
                        onChange={(option: any) => {
                          formik.setFieldValue('salesrepId', option?.value || '');
                        }}
                        placeholder={t('Choose')}
                      />
                      <div className='text-danger small'>{formik.touched.salesrepId && formik.errors.salesrepId}</div>
                    </div>

                    {/* شروط الدفع */}
                    <div className='col-lg-3 col-md-6 mb-3'>
                      <label className='form-label'>{t('PaymentTerms')}</label>
                      <Form.Select
                        name='paymentTermsId'
                        onChange={formik.handleChange}
                        value={formik.values.paymentTermsId}
                        isInvalid={formik.touched.paymentTermsId && !!formik.errors.paymentTermsId}
                      >
                        <option value=''>{t('Choose')}</option>
                        {PaymentTermsList?.map(item => (
                          <option key={item.id} value={item.id}>
                            {i18n.language === 'ar' ? item.name2 || item.name : item.name || item.name2}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type='invalid'>{formik.errors.paymentTermsId}</Form.Control.Feedback>
                    </div>

                    {/* طريقة الدفع */}
                    <div className='col-lg-3 col-md-6 mb-3'>
                      <label className='form-label'>{t('PaymentMethod')}</label>
                      <Form.Select
                        name='paymentMethodId'
                        onChange={formik.handleChange}
                        value={formik.values.paymentMethodId}
                        isInvalid={formik.touched.paymentMethodId && !!formik.errors.paymentMethodId}
                      >
                        <option value=''>{t('Choose')}</option>
                        {PaymentMethodList?.map((item: any) => (
                          <option key={item.id} value={item.id}>
                            {i18n.language === 'ar' ? item.name2 || item.name : item.name || item.name2}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type='invalid'>{formik.errors.paymentMethodId}</Form.Control.Feedback>
                    </div>

                    {/* العملة */}

                    {/* ('Currency') */}
                    <div className='col-lg-3'>
                      <div className='form-group mb-3'>
                        <label className='form-label'> {t('Currency')} </label>
                        <Form.Select
                          className='form-select'
                          name='currencyCode'
                          onChange={formik.handleChange}
                          value={formik.values.currencyCode || ''}
                        >
                          <option value=''>{i18n.language === 'ar' ? 'إختر' : 'Choose'}</option>
                          {Currency &&
                            Currency?.map((item: any, idx: number) => (
                              <option key={++idx} value={item.currencyCode}>
                                {item.description}
                              </option>
                            ))}
                        </Form.Select>
                        <div className='text-danger small'>
                          {formik.touched.currencyCode && formik.errors.currencyCode}
                        </div>
                      </div>
                    </div>

                    {/* ('معدل العملة') */}
                    <div className='col-lg-3 '>
                      <div className='form-group mb-3'>
                        <label className='form-label'> {t('CurrencyConversionRate')} </label>
                        <InputGroup>
                          <Form.Control
                            className='form-control text-center'
                            type='text'
                            readOnly
                            value={loadingRate ? '' : errorRate ? 'فشل التحميل' : rate}
                            placeholder={loadingRate ? 'جاري التحميل...' : ''}
                          />

                          {loadingRate && (
                            <InputGroup.Text>
                              <div className='spinner-border spinner-border-sm text-primary'></div>
                            </InputGroup.Text>
                          )}

                          {errorRate && !loadingRate && (
                            <InputGroup.Text style={{ padding: '0' }}>
                              <button
                                type='button'
                                className='btn btn-outline-warning h-100 px-2'
                                onClick={refetch}
                                title='إعادة تحميل سعر الصرف'
                              >
                                <i className='fas fa-redo'></i>
                              </button>
                            </InputGroup.Text>
                          )}
                        </InputGroup>

                        {errorRate && !loadingRate && (
                          <small className='text-warning mt-1 d-block text-center'>
                            <i className='fas fa-exclamation-circle me-1'></i>
                            تعذر تحميل السعر
                            <button
                              type='button'
                              className='btn btn-link btn-sm p-0 text-warning ms-1'
                              onClick={refetch}
                            >
                              (إعادة المحاولة)
                            </button>
                          </small>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* تنبيه الدفعات المسبقة */}
                  {hasPrepayments && (
                    <div className='alert alert-info d-flex align-items-center m-0'>
                      <i className='fas fa-info-circle me-2'></i>
                      <div>
                        <strong>{t('Reminder')}</strong> {t('هذا العميـل لديه دفعات مقدمة بإجمالي:')}
                        <span className='ms-2 text-success fw-bold'>{totalPrepaymentAmount}</span>
                      </div>
                    </div>
                  )}
                </CardBody>
                <CardFooter className='  p-0'>
                  <ul className='nav nav-tabs p-0'>
                    <li className='nav-item'>
                      <button
                        type='button'
                        className={`nav-link ${activeTab === 'arTransactionLines' ? 'active' : ''}`}
                        onClick={() => setActiveTab('arTransactionLines')}
                      >
                        <i className='fas fa-file-invoice me-2'></i>
                        {t('InvoiceLines')}
                      </button>
                    </li>

                    {formik.values.transactionType !== 'CreditMemo' && (
                      <li className='nav-item'>
                        <button
                          type='button'
                          className={`nav-link ${activeTab === 'arReceiptSchedules' ? 'active' : ''}`}
                          onClick={() => setActiveTab('arReceiptSchedules')}
                        >
                          <i className='fas fa-calendar-alt me-2'></i>
                          {t('InvoiceSchedule')}
                        </button>
                      </li>
                    )}
                  </ul>

                  <div className='tab-content py-4'>
                    {activeTab === 'arTransactionLines' && (
                      <div>
                        <ArTransactionsLine formik={formik} />
                        {formik.errors.arTransactionLinesList && (
                          <div className='text-danger small mt-2'>{t('InvoiceLinesRequired')}</div>
                        )}
                      </div>
                    )}

                    {activeTab === 'arReceiptSchedules' && (
                      <div className='p-2'>
                        <ArReceiptSchedules formik={formik} UnApplyPrepaymentsList={UnApplyPrepaymentsList} />
                        {formik.errors.arReceiptSchedulesList && (
                          <div className='text-danger small mt-2'>{t('InvoiceScheduleRequired')}</div>
                        )}
                      </div>
                    )}
                  </div>
                </CardFooter>

                {/* أزرار الإجراءات */}
                <div className='d-flex justify-content-center gap-3 mt-4'>
                  <button type='submit' disabled={formik.isSubmitting} className='btn btn-primary btn-lg'>
                    {formik.isSubmitting ? (
                      <>
                        <span className='spinner-border spinner-border-sm me-2' />
                        {t('Processing')}
                      </>
                    ) : (
                      <>
                        <i className='fas fa-plus me-2'></i>
                        {t('Add')}
                      </>
                    )}
                  </button>

                  <button
                    type='button'
                    className='btn btn-secondary btn-lg'
                    onClick={() => navigate('/AR/ArTransactions')}
                  >
                    <i className='fas fa-times me-2'></i>
                    {t('Cancel')}
                  </button>
                </div>
              </Form>
            </Loading>
          </Card>
        </>
      )}

      <Modal show={showModal} backdrop='static' keyboard={false} centered className='modal-selection-theme'>
        <Modal.Header className='bg-theme bg-opacity-20 text-dark py-3'>
          <Modal.Title className='text-center w-100 h5 mb-0'>
            <i className='fas fa-file-invoice me-2 text-theme'></i>
            {t('اختيار بيانات الفاتورة')}
          </Modal.Title>
        </Modal.Header>

        <Loading loading={ouLoading || customersLoading} error={ouError || customersError} Type='Dots'>
          <Modal.Body className='p-4'>
            <div className='text-center mb-4'>
              <div
                className='bg-theme bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3'
                style={{ width: '80px', height: '80px' }}
              >
                <i className='fas fa-clipboard-list fs-1 text-theme'></i>
              </div>
              <p className='text-muted mb-0'>{t('يجب اختيار الوحدة التشغيلية والعميل قبل المتابعة')}</p>
            </div>

            <div className='row'>
              {/* الوحدة التشغيلية */}
              <div className='col-12 mb-4'>
                <label className='form-label fw-bold'>{t('OperatingUnit')}</label>
                <div className='input-group'>
                  <span className='input-group-text bg-theme bg-opacity-10 border-end-0'>
                    <i className='fas fa-building text-theme'></i>
                  </span>
                  <Select
                    classNamePrefix='react-select'
                    isLoading={ouLoading}
                    isSearchable={true}
                    isClearable
                    options={OperatingUnitOptions}
                    value={OperatingUnitOptions?.find((f: any) => f.value === selectedOperatingUnit)}
                    onChange={option => {
                      setSelectedOperatingUnit(option?.value || '');
                    }}
                    placeholder={t('اختر وحدة التشغيل')}
                    className='flex-grow-1'
                  />
                </div>
              </div>

              {/* العميل */}
              <div className='col-12 mb-4'>
                <label className='form-label fw-bold'>{t('Customer')}</label>
                <div className='input-group'>
                  <span className='input-group-text bg-theme bg-opacity-10 border-end-0'>
                    <i className='fas fa-users text-theme'></i>
                  </span>
                  <TreeSelect
                    loading={customersLoading}
                    className='flex-grow-1'
                    placeholder={t('اختر العميـل')}
                    value={selectedCustomer}
                    onChange={setSelectedCustomer}
                    treeData={treeCustomerData}
                    treeNodeFilterProp='title'
                    showSearch
                    allowClear
                    dropdownStyle={{ zIndex: 1055 }}
                  />
                </div>
              </div>

              <div className='col-12 mb-3'>
                <label className='form-label fw-bold'>{t('InvoiceType')}</label>
                <div className='input-group'>
                  <span className='input-group-text bg-theme bg-opacity-10 border-end-0'>
                    <i className='fas fa-receipt text-theme'></i>
                  </span>
                  <div className='d-flex gap-2 p-2 border rounded flex-grow-1' style={{ minHeight: '44px' }}>
                    {/* زر الفاتورة القياسية */}
                    <input
                      type='radio'
                      className='btn-check'
                      name='transactionType'
                      id='transactionTypeStandard'
                      value='Standard'
                      checked={formik.values.transactionType === 'Standard'}
                      onChange={formik.handleChange}
                      autoComplete='off'
                    />
                    <label
                      className={`btn btn-sm ${
                        formik.values.transactionType === 'Standard' ? 'btn-theme' : 'btn-outline-theme'
                      } flex-fill d-flex align-items-center justify-content-center`}
                      htmlFor='transactionTypeStandard'
                      style={{ borderRadius: '1px', transition: 'all 0.3s ease' }}
                    >
                      {t('Standard')}
                    </label>

                    {/* زر إشعار المدين */}
                    <input
                      type='radio'
                      className='btn-check'
                      name='transactionType'
                      id='transactionTypeDebitMemo'
                      value='CreditMemo'
                      checked={formik.values.transactionType === 'CreditMemo'}
                      onChange={formik.handleChange}
                      autoComplete='off'
                    />
                    <label
                      className={`btn btn-sm ${
                        formik.values.transactionType === 'CreditMemo' ? 'btn-theme' : 'btn-outline-theme'
                      } flex-fill d-flex align-items-center justify-content-center`}
                      htmlFor='transactionTypeDebitMemo'
                      style={{ borderRadius: '1px', transition: 'all 0.3s ease' }}
                    >
                      {t('CreditMemo')}
                    </label>
                  </div>

                  {/* ✅ Hint تحت اختيار نوع العملية (AR) */}
                  {/* {formik.values.transactionType && (
                    <div className='mt-2 small text-muted'>
                      <i className='fas fa-info-circle me-2 text-theme'></i>

                      {formik.values.transactionType === 'Standard'
                        ? 'قياسي: فاتورة مبيعات/خدمة عادية على العميل'
                        : 'إشعار دائن: تطبيق خصم للعميل او مردود مبيعات.'}
                    </div>
                  )} */}
                  {formik.values.transactionType && (
                    <div className='mt-2 small text-muted'>
                      <i className='fas fa-info-circle me-2 text-theme'></i>

                      {formik.values.transactionType === 'Standard'
                        ? 'قياسي: فاتورة مبيعات/رسوم/خدمة عادية على العميل'
                        : 'إشعار دائن: تطبيق خصم للعميل او مردود مبيعات.'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Modal.Body>
        </Loading>

        <Modal.Footer className='justify-content-center border-top-0 pt-0'>
          <button
            className='btn btn-outline-secondary btn-lg px-4 py-2 me-3 rounded-2'
            onClick={() => navigate('/Ar/ArTransactions')}
          >
            <i className='fas fa-times me-2'></i>
            {t('Cancel')}
          </button>
          <button
            className='btn btn-theme btn-lg px-5 py-2 rounded-2'
            onClick={handleSubmitModal}
            disabled={!selectedCustomer || !selectedOperatingUnit || !formik.values.transactionType}
          >
            <i className='fas fa-check-circle me-2'></i>
            {t('Confirm')}
          </button>
        </Modal.Footer>
      </Modal>

      <style>
        {`
          .ant-select-outlined:not(.ant-select-customize-input) .ant-select-selector {
              border: 1px solid var(--bs-border-color);
              border-radius: 3px !important;
              height: 2rem;
          }
        `}
      </style>
    </>
  );
};
export default AddArTransactions;
